import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ListSkeleton } from "../components/Skeleton";

type Status = "Draft" | "InReview" | "Authorised" | "Archived";

interface EvidenceSummary {
  uuid: string;
  documentTitle: string;
  description: string | null;
  status: Status;
  currentVersionNumber: number | null;
  fileCount: number;
  linkedClauses: {
    uuid: string;
    standardCode: string;
    versionYear: number;
    clauseReference: string;
  }[];
  authorisedAt: string | null;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_TONE: Record<Status, string> = {
  Draft: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  InReview: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  Authorised: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  Archived: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const STATUS_FILTERS: Array<{ label: string; value: "" | Status }> = [
  { label: "All", value: "" },
  { label: "Draft", value: "Draft" },
  { label: "In review", value: "InReview" },
  { label: "Authorised", value: "Authorised" },
  { label: "Archived", value: "Archived" },
];

export function Vault() {
  const { slug } = useParams<{ slug: string }>();
  const [evidence, setEvidence] = useState<EvidenceSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [filter, setFilter] = useState<"" | Status>("");

  useEffect(() => {
    let cancelled = false;
    setEvidence(null);
    const path = filter ? `/evidence?status=${filter}` : "/evidence";
    api<{ evidence: EvidenceSummary[] }>(path)
      .then((res) => {
        if (!cancelled) setEvidence(res.evidence);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">The Vault</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Controlled documented information for your organisation. Each record can hold multiple
            files, links to ISO clauses, and an immutable history of authorised versions.
          </p>
        </div>
        <Link
          to={`/o/${slug}/vault/new`}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
        >
          New evidence
        </Link>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={[
              "rounded-md px-3 py-1 text-xs font-medium transition",
              filter === f.value
                ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
            ].join(" ")}
          >
            {f.label}
          </button>
        ))}
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {evidence === null ? (
        <ListSkeleton />
      ) : evidence.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
          No evidence yet. Click &quot;New evidence&quot; to add the first document.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2">Document</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Files</th>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-800">
              {evidence.map((e) => (
                <tr key={e.uuid} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="px-4 py-2 align-top">
                    <Link
                      to={`/o/${slug}/vault/${e.uuid}`}
                      className="font-medium text-slate-900 hover:text-brand-700 dark:text-slate-100 dark:hover:text-brand-300"
                    >
                      {e.documentTitle}
                    </Link>
                    {e.linkedClauses.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {e.linkedClauses.slice(0, 3).map((c) => (
                          <span
                            key={c.uuid}
                            className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                          >
                            {c.standardCode.replace(/^ISO/, "ISO ")}:{c.versionYear} cl.{" "}
                            {c.clauseReference}
                          </span>
                        ))}
                        {e.linkedClauses.length > 3 ? (
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">
                            +{e.linkedClauses.length - 3} more
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[e.status]}`}
                    >
                      {e.status === "InReview" ? "In review" : e.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-top text-slate-600 dark:text-slate-300">
                    {e.fileCount}
                  </td>
                  <td className="px-4 py-2 align-top text-slate-600 dark:text-slate-300">
                    {e.currentVersionNumber ? `v${e.currentVersionNumber}` : "—"}
                  </td>
                  <td className="px-4 py-2 align-top text-slate-600 dark:text-slate-300">
                    {formatDateUk(e.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
